import React, { useContext, useEffect } from 'react';
import { CardInfo } from '../components/CardInfo';
import ChartProgressBar from '../components/ChartProgressBar';
import ApproveProjects from '../components/ApproveProjects';
import PendingChart from '../components/PendingChart';
import CompositionWalletCard from '../components/CompositionWalletCard';
import { OnePagerDoughnutChart } from '../components/OnePagerDoughnutChart';
import { useNumberFormatter } from '../../../../hooks/useNumberFormatter';
import { useTranslation } from 'react-i18next';
import HeaderReport from '../components/HeaderReport';
import ReusableIcon from '../../../Reports/ExtemdedReport/components/shared/ReusableIcon';
import { ReactComponent as IconSatis } from '../../../../assets/icons/u_alert-check.svg'
import { TagFilterContext } from '../../../../context/TagFilterContext';
import { ExpiredDisbursementsCard } from '../../../Reports/ExtemdedReport/components/GeneralInformation/shared/ExpiredDisbursementsCard';

export const gradientColors = ['#0b396e', '#3975c8', '#00CFDD', '#f39f2e', '#ffd700'];

interface OnePagerReportProps {
  data: {
    loan?: any;
    igr?: any;
    tcp?: any;
  };
  title: string;
  generalData: any;
  filtersApplied?: any;
  portfolioName: any;
  isPdfExport?: boolean;
}

const OnePagerReportLoan: React.FC<OnePagerReportProps> = ({ data, title, generalData, filtersApplied, portfolioName, isPdfExport = false }) => {
  const { formatMillions } = useNumberFormatter();
  const { labels } = useContext(TagFilterContext);
  const { t } = useTranslation();

  const renderSection = (sectionData: any, title: string) => {
    const { bidAmount, composition, approvedprojects, singleCard, pmr, sectors, disbursementAndProjections } = sectionData;
    if (!sectionData) return null;

    const dataSize = sectors.length;

    return (
      <div className="section content-background">
        <header className="header-content">
          <p>{t('one_pager_report.lon_title')}</p>
          <ReusableIcon
            title={title}
            color="#BE86D8"
          />
        </header>

        <div className="c-charts-loan">
          {/* Composition */}
          <div className="composition">
            <div className="chart-row">
              <h3 className="chart-title">{t('one_pager_report.composition')}</h3>
              <div className="chart-content">
                <CardInfo title={t('one_pager_report.projects')} data={composition?.projects ?? 0} />
                <CardInfo title={t('one_pager_report.operations')} data={composition?.operations ?? 0} />
                <CardInfo
                  title={t('one_pager_report.years')}
                  data={composition?.years ?? 0}
                  units={
                    (composition?.years ?? 0) > 1
                      ? t('one_pager_report.antiquity_years')
                      : t('one_pager_report.antiquity_year')
                  }
                  description={t('one_pager_report.no-warranties-included')}
                />
              </div>
            </div>
            <div className="chart-row">
              <h3 className="chart-title">{t('one_pager_report.breakdown_operations')}</h3>
              <div className="chart-content">
                <CardInfo title={t('one_pager_report.guarantees')} data={composition?.guarantees ?? 0} />
                <CardInfo title={t('one_pager_report.investment')} data={composition?.investment ?? 0} />
                <CardInfo title="PBP" data={composition?.pbp ?? 0} />
              </div>
            </div>
          </div>

          {/* BID Amount */}
          <div className="amounts">
            <div>
              <h3 className="chart-title">{t('one_pager_report.bid_amount')}</h3>
              <div className="chart-content" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr' }}>
                <CardInfo
                  title={t('one_pager_report.original')}
                  data={`${formatMillions(bidAmount?.originalAmount)} MM`}
                />
                <CardInfo
                  title={t('one_pager_report.current_title')}
                  data={`${formatMillions(bidAmount?.currentAmount)} MM`}
                />
              </div>
            </div>
            <div>
              <h3 className="chart-title">{t('one_pager_report.current_approve_amount')}</h3>
              <div className="chart-content">
                <CardInfo
                  title={t('one_pager_report.warranty')}
                  data={`${formatMillions(bidAmount?.guaranteesAmount)} MM`}
                />
                <CardInfo
                  title={`${t('one_pager_report.investment')} (USD)`}
                  data={`${formatMillions(bidAmount?.pbpInvestment)} MM`}
                />
                <CardInfo title="PBP (USD)" data={`${formatMillions(bidAmount?.pbpAmount)} MM`} />
              </div>
            </div>
            <div>
              <h3 className="chart-title">{t('one_pager_report.accumulate_disbursements')}</h3>
              <div className="chart-content">
                <ChartProgressBar
                  completed={bidAmount?.disbursementPercent}
                  totalAmount={bidAmount?.disbursementAmount + bidAmount?.undisburstmentAmount}
                  disbursmentAmount={bidAmount?.disbursementAmount}
                />
              </div>
            </div>
          </div>

          <div className="approve-projects">
            <div>
              <h3 className="chart-title mb-2">{t('one_pager_report.approved_proyects')}</h3>
              <div className="c-approve-projects">
                <ApproveProjects
                  projects={approvedprojects?.aprprovedProjectCount}
                  title={t('one_pager_report.number_approved_projects')}
                  value={formatMillions(approvedprojects?.aprprovedProjectAmount)}
                />
                <ApproveProjects
                  projects={approvedprojects?.preparationProjectCount}
                  title={`${t('one_pager_report.projects_in_preparation')} (Pipeline A)`}
                  value={formatMillions(approvedprojects?.preparationProjectAmount)}
                />
              </div>
            </div>
          </div>

          <div className="pending">
            <div className="c-pending" style={{ flex: 1 }}>
              <PendingChart
                title={t('one_pager_report.pending_legal_validity')}
                value={singleCard?.pendingLegalValidity}
              />
            </div>
          </div>

          <CompositionWalletCard
            sectors={sectors}
            colors={gradientColors.slice(0, dataSize)}
            dataSize={dataSize}
            isPdfExport={isPdfExport}
          />

          <ExpiredDisbursementsCard quantity={singleCard?.projectsOverdueDisbursement} />

          <div className="disbursement">
            <h3 className="chart-title">{t('one_pager_report.year_disbursements')}</h3>
            <div className="chart-content">
              <ChartProgressBar
                format='USD'
                completed={disbursementAndProjections.projectionsDisbursementsYearPercent}
                totalAmount={disbursementAndProjections.projectionsDisbursementsYearAmount + disbursementAndProjections.projectionsWithoutDisbursementsYearAmount}
                disbursmentAmount={disbursementAndProjections.projectionsDisbursementsYearAmount} />
            </div>
          </div>

          <div className="pmr">
            <div className='pmrIconContent'>
              <p>PMR</p>
              <IconSatis />
            </div>
            <p>{t('extended_report.PMR_description')}</p>
            <OnePagerDoughnutChart data={pmr} />
          </div>
        </div>
      </div>
    );
  };

  return (
    <section className="c-main">
      <HeaderReport
        portfolioName={portfolioName}
        filtersApplied={filtersApplied ?? labels}
        generalData={generalData}
      />

      <article className="content">{data.loan && renderSection(data.loan, title)}</article>
    </section>
  );
};

export default OnePagerReportLoan;
